import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECT_META = {
  maths:   { emoji: '➗', color: 'bg-amber-100 text-amber-900' },
  english: { emoji: '📖', color: 'bg-emerald-100 text-emerald-900' },
  science: { emoji: '🔬', color: 'bg-sky-100 text-sky-900' },
  scratch: { emoji: '🐱', color: 'bg-violet-100 text-violet-900' },
};

const FALLBACK_IMG = '/marketing/hero-11-kid-pointing-laptop.jpeg';

export default function Lessons() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const regId = useMemo(() => {
    try { return localStorage.getItem('gop_registration_id'); } catch (e) { return null; }
  }, []);
  const [lessons, setLessons] = useState([]);
  const [subject, setSubject] = useState('all');
  const [progress, setProgress] = useState({ completed: new Set(), best: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Sprint 10.5 (post-Wingman revert) — lesson content is English-only
        // by policy. `?lang=en` is explicit and no re-fetch on locale switch.
        const { data: L } = await axios.get(`${API}/quiz/lessons?lang=en`);
        setLessons(L.lessons || []);
        if (regId) {
          const { data: P } = await axios.get(`${API}/quiz/progress/${regId}`);
          const completed = new Set();
          if (Array.isArray(P.lessons_completed)) {
            P.lessons_completed.forEach(id => completed.add(id));
          }
          setProgress({ completed, best: {} });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [regId]);

  if (!regId) return <Navigate to="/register" replace />;

  const filtered = subject === 'all' ? lessons : lessons.filter(l => l.subject === subject);

  return (
    <div className="min-h-screen bg-background px-4 py-8" data-testid="lessons-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold gop-fade-in-up" data-testid="lessons-heading">
              {t('lessons.title') || 'Lessons'}
            </h1>
            <p className="text-sm text-muted-foreground gop-fade-in-up" style={{ animationDelay: '80ms' }}>
              {t('lessons.subtitle') || 'Pick a lesson. Complete the quiz. Watch your progress grow.'}
            </p>
          </div>
          <div className="flex gap-2">
            {['all', 'maths', 'english', 'science', 'scratch'].map(s => (
              <button
                key={s}
                onClick={() => setSubject(s)}
                data-testid={`filter-${s}-btn`}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                  subject === s ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-slate-50'
                }`}
              >
                {t(`lessons.filter.${s}`) || s}
              </button>
            ))}
            <Button variant="outline" onClick={() => navigate(`/progress/${regId}`)} data-testid="go-progress-btn">
              {t('lessons.progress.cta') || 'My Progress →'}
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading') || 'Loading…'}</p>
        ) : (
          <>
          <div className="mb-4 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
               data-testid="en-only-content-notice">
            {(() => {
              const base = String(i18n.language || 'en').toLowerCase();
              const isBilingual = base === 'si' || base === 'si-en-dual' || base === 'ta' || base === 'ta-en-dual';
              if (isBilingual) {
                return base.startsWith('si')
                  ? 'ද්විභාෂා පාඩම්: ගණිතය · විද්‍යාව · Scratch ඔබේ භාෂාවෙන් + English. English පාඩම් — English වලින්ම.'
                  : 'இருமொழி பாடங்கள்: கணிதம் · அறிவியல் · Scratch உங்கள் மொழியில் + English. English பாடங்கள் — English இலேயே.';
              }
              return t('lessons.en_only_notice') ||
                'Lessons are delivered in English — your progress and support are in your language.';
            })()}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((l, idx) => {
              const meta = SUBJECT_META[l.subject] || {};
              const done = progress.completed.has(l.id);
              const img = l.lesson_image_url || FALLBACK_IMG;
              return (
                <Card
                  key={l.id}
                  className="card-shadow cursor-pointer overflow-hidden gop-fade-in-up gop-hover-lift"
                  style={{ animationDelay: `${Math.min(idx, 12) * 40}ms` }}
                  data-testid={`lesson-card-${l.id}`}
                  onClick={() => navigate(`/quiz/${l.id}`)}
                >
                  <div className="w-full aspect-[16/9] bg-slate-100 relative">
                    <img
                      src={img}
                      alt={l.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      data-testid={`lesson-hero-${l.id}`}
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    />
                    {done && (
                      <Badge
                        className="absolute top-2 right-2 shadow"
                        variant="default"
                        data-testid="lesson-done-badge"
                      >
                        ✓ {t('lessons.done') || 'Done'}
                      </Badge>
                    )}
                  </div>
                  <CardHeader className="pb-2">
                    <span className={`text-xs px-2 py-0.5 rounded self-start ${meta.color || 'bg-muted'}`}>
                      {meta.emoji} {l.subject}
                    </span>
                    <CardTitle className="text-lg mt-2">{l.title}</CardTitle>
                    <CardDescription>
                      {t('lessons.qcount', { count: l.question_count }) || `${l.question_count} questions · ${l.duration_minutes} min`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button size="sm" className="w-full" data-testid={`start-quiz-${l.id}`}>
                      {done ? (t('lessons.retry') || 'Retry quiz') : (t('lessons.start') || 'Start lesson')}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
